import { supabase } from '@/lib/supabase';

// ─────────────────────────────────────────────────────────────
// ZIFEK CORE — 66. PERFORMANCE
// Zifek Core doit soutenir PROGRESSIVEMENT : milliers de businesses,
// centaines de milliers d’utilisateurs, millions de clients, millions de
// commandes, millions d’événements.
// On ne construit JAMAIS l’architecture autour d’un nombre fixe de clients :
// l’échelle est un continuum, pas une constante.
// ─────────────────────────────────────────────────────────────

export interface CorePerformanceTarget {
  id: number;
  metric_key: string;
  label: string | null;
  category: string | null;
  unit: string | null;
  target_value: number | null;
  horizon: string | null;
  notes: string | null;
  sort_order: number | null;
}

export async function listPerformanceTargets(): Promise<CorePerformanceTarget[]> {
  const { data, error } = await supabase
    .from('core_performance_targets')
    .select('*')
    .order('sort_order', { ascending: true });
  if (error) throw error;
  return (data || []) as CorePerformanceTarget[];
}

export interface CreateTargetInput {
  metricKey: string;
  label: string;
  category: string;
  unit: string;
  targetValue: number;
  horizon?: string | null;
  notes?: string | null;
}

export async function createPerformanceTarget(input: CreateTargetInput): Promise<void> {
  const { error } = await supabase.from('core_performance_targets').insert({
    metric_key: input.metricKey,
    label: input.label,
    category: input.category,
    unit: input.unit,
    target_value: input.targetValue,
    horizon: input.horizon ?? 'Permanent',
    notes: input.notes ?? null,
    sort_order: 99,
  });
  if (error) throw error;
}

export async function deletePerformanceTarget(id: number): Promise<void> {
  const { error } = await supabase.from('core_performance_targets').delete().eq('id', id);
  if (error) throw error;
}

/** Piliers d’architecture pour l’échelle (documentation vivante, spec 66). */
export const SCALE_PILLARS: { key: string; label: string; icon: string; points: string[] }[] = [
  {
    key: 'isolation', label: 'Isolation par tenant', icon: 'ri-layout-grid-line',
    points: ['Chaque requête est scopée par organization_id.', 'Aucune jointure inter-tenant non contrôlée.', 'Le cache est préfixé par le tenant.'],
  },
  {
    key: 'pagination', label: 'Pagination systématique', icon: 'ri-list-check-2',
    points: ['Toujours paginer, jamais charger tout.', 'Curseurs plutôt qu’offset sur les gros volumes.', 'Limites de fenêtre sur les listes d’événements.'],
  },
  {
    key: 'index', label: 'Indexation ciblée', icon: 'ri-node-tree',
    points: ['Index sur organization_id et les clés de tri.', 'Index composites pour les filtres fréquents.', 'Index uniques pour les contraintes métier.'],
  },
  {
    key: 'async', label: 'Traitement asynchrone', icon: 'ri-timer-flash-line',
    points: ['Les événements et notifications sont best-effort.', 'Les tâches lourdes ne bloquent jamais la requête.', 'File d’attente pour les envois massifs.'],
  },
  {
    key: 'observability', label: 'Observabilité', icon: 'ri-pulse-line',
    points: ['Latence p95 suivie par endpoint.', 'Alerte sur pics d’erreurs.', 'Détection des fuites d’isolation.'],
  },
];

export interface PerformanceStats {
  targets: number;
  scaleData: number;
  latencyData: number;
  reliabilityData: number;
}

export async function fetchPerformanceStats(): Promise<PerformanceStats> {
  const targets = await listPerformanceTargets();
  return {
    targets: targets.length,
    scaleData: targets.filter((t) => t.category === 'Échelle').length,
    latencyData: targets.filter((t) => t.category === 'Latence').length,
    reliabilityData: targets.filter((t) => t.category === 'Fiabilité').length,
  };
}